import { TabContext } from "@mui/lab";
import { Box, type BoxProps, MenuItem, Popover, Stack } from "@mui/material";

import { type FC, type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ContentTone } from "../brand-kit/server/brand-kit-service";
import { CommsMarkdownContent } from "./comms-markdown-content";
import { CommsTemplateImage } from "./comms-template-image";
import { CommsTemplateTones } from "./comms-template-tones";
import { useGenerateCommsContext } from "./generate-comms-context";
import { AITemplateContentTabPanel } from "./template-content-tab-panel";
import { TemplateSubject } from "./template-subject";

export const CommsTemplateContent: FC<
  {
    contentTone: string;
    onContentToneChange?: (value: ContentTone | "custom") => void;
  } & BoxProps
> = ({
  contentTone,

  onContentToneChange,
  ...boxProps
}) => {
  const { t } = useTranslation();
  const {
    format,
    tones: { customTones, selectedTone, setSelectedTone },
    previewImages: { images },
  } = useGenerateCommsContext();

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const handleClick = (event: MouseEvent<Element>): void => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  if (!contentTone) {
    return null;
  }

  return (
    <>
      <Box {...boxProps}>
        <TabContext value={contentTone}>
          <Stack gap={2} mt={2}>
            {format === "Communication" ? (
              <TemplateSubject subject={images?.find((x) => x !== undefined)?.data?.subjectLine} />
            ) : null}
            <CommsTemplateImage format={format} />

            <CommsTemplateTones
              contentTone={contentTone as ContentTone}
              onContentToneChange={onContentToneChange}
              selectedBrandTone={selectedTone}
              customBrandTones={customTones}
              handleClick={handleClick}
            />
          </Stack>
          {selectedTone && (
            <AITemplateContentTabPanel
              sx={{ p: 0 }}
              value={"custom"}
              contentTone="custom"
              title={selectedTone.name}
            >
              <CommsMarkdownContent tone={selectedTone.summary} format={format} />
            </AITemplateContentTabPanel>
          )}

          <AITemplateContentTabPanel
            sx={{ p: 0 }}
            value="professional"
            contentTone="professional"
            title={t("Professional")}
          >
            <CommsMarkdownContent tone={contentTone} format={format} />
          </AITemplateContentTabPanel>
          <AITemplateContentTabPanel
            sx={{ p: 0 }}
            value="casual"
            contentTone="casual"
            title={t("Casual")}
          >
            <CommsMarkdownContent tone={contentTone} format={format} />
          </AITemplateContentTabPanel>
          <AITemplateContentTabPanel
            sx={{ p: 0 }}
            value="direct"
            contentTone="direct"
            title={t("Direct")}
          >
            <CommsMarkdownContent tone={contentTone} format={format} />
          </AITemplateContentTabPanel>
        </TabContext>
      </Box>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {customTones?.map((tone) => (
          <MenuItem
            key={tone.id}
            selected={tone.id === selectedTone?.id}
            onClick={() => {
              setAnchorEl(null);
              void setSelectedTone?.(tone.id);
            }}
          >
            {tone.name}
          </MenuItem>
        ))}
      </Popover>
    </>
  );
};
